import Vue from 'vue'
import App from './App.vue'
import VueRouter from 'vue-router'
import Routes from './routes/'
import axios from 'axios'
import { createStore } from './store/'
import { getAccessToken, getRefreshToken, updateAccessToken, clearSession } from './utils/auth'
import BootstrapVue from 'bootstrap-vue'
import $ from 'jquery'

import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'

import moment from 'moment'

import VueResource from 'vue-resource'

Vue.use(VueResource);

axios.defaults.baseURL = process.env.VUE_APP_SERVICE_URL

// ✅ FIXED: Check if $http already exists before setting
// If it's already a getter, don't override it
if (!Vue.prototype.hasOwnProperty('$http')) {
  Vue.prototype.$http = axios;
}

Vue.config.productionTip = false

Vue.filter('formatDate', function(value) {
  if (value) {
    return moment(String(value)).fromNow();
  }
});

Vue.use(BootstrapVue)
Vue.use(VueRouter)
// Vue.component('font-awesome-icon', FontAwesomeIcon)

const router = new VueRouter({
  routes: Routes,
  mode: 'history'
});

/**
 * ROUTE GUARD
 *
 * Protection used to be per-component and inconsistent: AdminDashboard checked
 * `this.token` (a property it never declared, so the check never ran), Content.vue
 * compared against "" (never true when the key is absent and getItem returns null),
 * and several actions hard-navigated to /logout on any failure.
 *
 * One guard, by path prefix, so a new screen under /app or /merchant- is protected
 * automatically without remembering to add meta to its route.
 */
const PROTECTED_PREFIXES = ['/app', '/merchant-', '/settings'];

function requiresAuth(path) {
  return PROTECTED_PREFIXES.some(function (p) { return String(path || '').indexOf(p) === 0; });
}

router.beforeEach(function (to, from, next) {
  if (requiresAuth(to.path) && !getAccessToken()) {
    next({ name: 'login', query: { next: to.fullPath } });
    return;
  }
  next();
});

const store = createStore()

import './css/style.scss'
import './assets/scss/style.scss'

// ✅ COMPREHENSIVE: REQUEST INTERCEPTOR
// Adds token to all requests + logs requests
axios.interceptors.request.use(
  (config) => {
    // Add authorization token to all requests
    const token = getAccessToken();
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
      // Remember which token this request used. If it comes back 401 and the stored
      // token has changed in the meantime, another request already refreshed it and
      // this one only needs replaying - not a second refresh.
      config._authToken = token;
    }

    // Add content type
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    // Log request (dev only)
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/* ===================================================================
 * TOKEN REFRESH
 *
 * The old behaviour was: ANY 401 from ANY request deleted the token. A single
 * expired-token response - even from a stray call on the public home page - wiped
 * the session, and the next screen that needed a token bounced the user to /logout.
 * That is the "logged out when I come back from the home page" bug.
 *
 * Now a 401 tries POST auth/token/refresh once, replays the original request with
 * the new access token, and only signs the user out if the refresh itself fails.
 *
 * Concurrency matters: a dashboard fires several requests at once, and all of them
 * can 401 together. `refreshPromise` makes them share ONE refresh call instead of
 * firing five and invalidating each other.
 * =================================================================== */
const REFRESH_URL = process.env.VUE_APP_AUTH_REFRESH_URL || 'auth/token/refresh';
let refreshPromise = null;

/** Requests that must never trigger a refresh (they are the auth flow itself). */
function isAuthEndpoint(url) {
  const u = String(url || '');
  return u.indexOf(REFRESH_URL) !== -1 ||
         u.indexOf('auth/login') !== -1 ||
         u.indexOf('auth/register') !== -1 ||
         u.indexOf('auth/verify-otp') !== -1 ||
         u.indexOf('auth/resend-otp') !== -1;
}

function refreshAccessToken() {
  if (refreshPromise) { return refreshPromise; }
  const refresh = getRefreshToken();
  if (!refresh) { return Promise.reject(new Error('no-refresh-token')); }

  refreshPromise = axios
    .post(REFRESH_URL, { refresh: refresh }, { skipAuth: true })
    .then((response) => {
      const data = response.data || {};
      const access = data.access || (data.tokens && data.tokens.access);
      if (!access) { throw new Error('refresh-no-access'); }
      updateAccessToken(access);
      axios.defaults.headers.common.Authorization = 'Bearer ' + access;
      return access;
    })
    .then((access) => { refreshPromise = null; return access; })
    .catch((err) => { refreshPromise = null; throw err; });

  return refreshPromise;
}

function endSession() {
  clearSession();
  const path = window.location.pathname || '';
  // Do not bounce someone off a public page just because a background call failed.
  const onProtectedPage = /^\/(app|merchant-|settings)/.test(path);
  if (onProtectedPage && path.indexOf('/login') === -1) {
    const next = encodeURIComponent(path + (window.location.search || ''));
    window.location.replace('/login?next=' + next);
  }
}

// ✅ COMPREHENSIVE: RESPONSE INTERCEPTOR
// Handles responses globally + error handling
axios.interceptors.response.use(
  (response) => {
    // Log successful responses (dev only)
    if (process.env.NODE_ENV === 'development') {
      console.log(`📥 API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Handle different error types globally
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;
    const original = error.config || {};

    // 401 -> refresh once, then replay the original request.
    if (status === 401 && !original._retried && !isAuthEndpoint(original.url) && getRefreshToken()) {
      original._retried = true;

      // Another in-flight request may have refreshed already. Replay with the current
      // token rather than refreshing again - otherwise a dashboard firing five calls
      // at once triggers several refreshes, and each one can invalidate the last.
      const current = getAccessToken();
      if (current && original._authToken && current !== original._authToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = 'Bearer ' + current;
        return axios(original);
      }

      return refreshAccessToken()
        .then((access) => {
          original.headers = original.headers || {};
          original.headers.Authorization = 'Bearer ' + access;
          return axios(original);
        })
        .catch(() => {
          console.warn('[auth] refresh failed - signing out');
          endSession();
          return Promise.reject(error);
        });
    }

    if (status === 401 && !getRefreshToken() && !isAuthEndpoint(original.url)) {
      // Nothing to refresh with. Only leave the page if we are actually on one
      // that requires a session.
      endSession();
    }

    switch (status) {
      case 401:
        // Handled above by the refresh flow. Reaching here means the refresh was
        // already tried and failed, so there is nothing left to log.
        break;

      case 403:
        // Permission denied
        console.warn('🚫 Forbidden (403) - User lacks permission');
        break;

      case 404:
        // Resource not found
        console.warn('❓ Not Found (404) - Resource does not exist');
        break;

      case 400:
        // Bad request - validation error
        console.warn('⚠️ Bad Request (400) - Validation error:', message);
        break;

      case 429:
        // Rate limited
        console.warn('⏱️ Rate Limited (429) - Too many requests');
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server error
        console.error('🔴 Server Error (' + status + ')', message);
        break;

      default:
        if (error.message === 'Network Error') {
          console.error('🌐 Network Error - Check your internet connection');
        } else {
          console.error('❌ API Error:', message || error.message);
        }
    }

    // Always reject with full error object for component handling
    return Promise.reject(error);
  }
);

new Vue({
  store,
  router: router,
  render: h => h(App)
}).$mount('#app')

// External JS File //
$(document).ready(function(){
$(window).scroll(function(){
    if($(this).scrollTop() > 80){
        $('.navbar').addClass('bg-light');
    }
    else{
        if ($(window).width() > 992) {
            $('.navbar').removeClass('bg-light');
        }
    }
});
});

if ($(window).width() < 992) {
$(".navbar-toggler").on("click",function(){
    $('.navbar').addClass('bg-light');
})
}

$(document).ready(function(){
$('.navbar-nav>li>a').on('click', function(){
    $('.navbar-collapse').collapse('hide');
});
});


let section1 = document.querySelector('#banner');
let section2 = document.querySelector('#about');
let section3 = document.querySelector('#features');
let section4 = document.querySelector('#platform');

window.addEventListener('scroll',()=>{
    let windowState = Math.floor(window.pageYOffset) + 1;

    // Guard against pages that don't render these sections (prevents "Cannot read offsetTop of null")
    if(!section2 || !section3 || !section4){
        return;
    }

    if(section2 && section2.offsetTop <= windowState && section3.offsetTop > windowState){
        $('.nav-link').removeClass('active');
         $('li.nav-item:nth-child(2)').children().addClass('active');
    }
    else if(section3 && section3.offsetTop <= windowState && section4.offsetTop > windowState){
        $('.nav-link').removeClass('active');
        $('li.nav-item:nth-child(3)').children().addClass('active');
    }
    else if(section4 && section4.offsetTop <= windowState){
        $('.nav-link').removeClass('active');
        $('li.nav-item:nth-child(4)').children().addClass('active');
    }
    else{
        $('.nav-link').removeClass('active');
        $('li.nav-item:nth-child(1)').children().addClass('active');
    }
})
